import { CompanyManagerAppraisalMeetingStatus, CompanyManagerAppraisalMeetingStatusTableRowDto } from '../../../types'
import type { AppraisalMonitoringFiltersState } from '../dasboardStoreTypes'

import type { Colors, IconNames } from '../../../components/util/types'

export type AppraisalMonitoringTableStatusFilter = CompanyManagerAppraisalMeetingStatus

export const MONITORING_TABLE_STATUS_OPTIONS: AppraisalMonitoringTableStatusFilter[] = [
  CompanyManagerAppraisalMeetingStatus.NotInvited,
  CompanyManagerAppraisalMeetingStatus.Outdated,
  CompanyManagerAppraisalMeetingStatus.NotPrepared,
  CompanyManagerAppraisalMeetingStatus.Prepared,
  CompanyManagerAppraisalMeetingStatus.WaitingForEmployeeConfirmation,
  CompanyManagerAppraisalMeetingStatus.WaitingForHrResult,
  CompanyManagerAppraisalMeetingStatus.HavingAnAppraisalUpdate,
  CompanyManagerAppraisalMeetingStatus.Done,
]

/** Histogram “SCHEDULED” band: same order as `MONITORING_TABLE_STATUS_OPTIONS`, excludes NotInvited and post-invite statuses. */
export const MONITORING_CHART_INVITES_GROUP: readonly AppraisalMonitoringTableStatusFilter[] = MONITORING_TABLE_STATUS_OPTIONS.slice(
  1,
  4,
) as AppraisalMonitoringTableStatusFilter[]

export const MONITORING_CHART_HIDE_WHEN_ZERO: ReadonlySet<AppraisalMonitoringTableStatusFilter> = new Set([
  CompanyManagerAppraisalMeetingStatus.Outdated,
  CompanyManagerAppraisalMeetingStatus.HavingAnAppraisalUpdate,
  CompanyManagerAppraisalMeetingStatus.WaitingForHrResult,
])

/** Visibility uses company-wide counts, ignoring Filters Area. */

export const isMonitoringStatusVisible = (status: AppraisalMonitoringTableStatusFilter, count: number): boolean =>
  count > 0 || !MONITORING_CHART_HIDE_WHEN_ZERO.has(status)

export const visibleMonitoringStatusOptions = (
  unfilteredStatusCounts: ReadonlyMap<AppraisalMonitoringTableStatusFilter, number>,
): AppraisalMonitoringTableStatusFilter[] =>
  MONITORING_TABLE_STATUS_OPTIONS.filter((status) => isMonitoringStatusVisible(status, unfilteredStatusCounts.get(status) ?? 0))

export const resolveAppraisalMonitoringTableStatus = (
  status: AppraisalMonitoringTableStatusFilter | undefined,
  rows: CompanyManagerAppraisalMeetingStatusTableRowDto[],
): AppraisalMonitoringTableStatusFilter | undefined => {
  if (!status) {
    return undefined
  }
  const count = rows.some((row) => row.status === status) ? 1 : 0
  return isMonitoringStatusVisible(status, count) ? status : undefined
}

export const isMonitoringChartInvitesStatus = (status: AppraisalMonitoringTableStatusFilter): boolean =>
  (MONITORING_CHART_INVITES_GROUP as readonly AppraisalMonitoringTableStatusFilter[]).includes(status)

/** Filters-area / store `appraisalMonitoringFilters` only (excludes table “Filter by status”). */
export function hasAppraisalMonitoringFilterAreaValues(filters: AppraisalMonitoringFiltersState): boolean {
  return !!(filters.selectedBranchOffice || filters.selectedBusinessUnit || filters.selectedBuManager || filters.selectedEmployeeLevels)
}

/** Maps appraisal meeting status to dashboard SVG icons (`icons.ts`). */
export const appraisalMeetingStatusToDashboardIcon = (status: AppraisalMonitoringTableStatusFilter): IconNames => {
  const map: Record<AppraisalMonitoringTableStatusFilter, IconNames> = {
    [CompanyManagerAppraisalMeetingStatus.NotInvited]: 'dashboard_notInvited',
    [CompanyManagerAppraisalMeetingStatus.Outdated]: 'dashboard_outdated',
    [CompanyManagerAppraisalMeetingStatus.NotPrepared]: 'dashboard_notPrepared_stroke',
    [CompanyManagerAppraisalMeetingStatus.Prepared]: 'dashboard_preparedByManager',
    [CompanyManagerAppraisalMeetingStatus.WaitingForEmployeeConfirmation]: 'dashboard_waitingForEmployee',
    [CompanyManagerAppraisalMeetingStatus.WaitingForHrResult]: 'dashboard_waitingForHrResult',
    [CompanyManagerAppraisalMeetingStatus.HavingAnAppraisalUpdate]: 'dashboard_disputed',
    [CompanyManagerAppraisalMeetingStatus.Done]: 'dashboard_done',
  }

  return map[status] ?? 'dashboard_done'
}

/** Statuses shown when “attention points only” is on */
export const APPRAISAL_MONITORING_ATTENTION_POINTS_TOGGLE_STATUSES: ReadonlySet<AppraisalMonitoringTableStatusFilter> = new Set([
  CompanyManagerAppraisalMeetingStatus.NotInvited,
  CompanyManagerAppraisalMeetingStatus.Outdated,
  CompanyManagerAppraisalMeetingStatus.HavingAnAppraisalUpdate,
])

export const isAttentionPointStatus = (status: AppraisalMonitoringTableStatusFilter): boolean =>
  APPRAISAL_MONITORING_ATTENTION_POINTS_TOGGLE_STATUSES.has(status)

export type AppraisalMonitoringStatusAccent = 'default' | 'attention' | 'attention-critical' | 'closed'

/** Expired stays error-red; other attention points use warning gold; Closed uses pyramid-value blue. */
export const appraisalMonitoringStatusAccent = (status: AppraisalMonitoringTableStatusFilter): AppraisalMonitoringStatusAccent => {
  if (status === CompanyManagerAppraisalMeetingStatus.Outdated) {
    return 'attention-critical'
  }
  if (isAttentionPointStatus(status)) {
    return 'attention'
  }
  if (status === CompanyManagerAppraisalMeetingStatus.Done) {
    return 'closed'
  }
  return 'default'
}

export const appraisalMonitoringStatusIconColor = (status: AppraisalMonitoringTableStatusFilter): Colors => {
  const accent = appraisalMonitoringStatusAccent(status)
  if (accent === 'attention-critical') {
    return 'error'
  }
  if (accent === 'attention') {
    return 'warning'
  }
  if (accent === 'closed') {
    return 'monitoring-closed'
  }
  return 'monitoring-neutral'
}

function matchesAppraisalMonitoringFiltersArea(
  row: CompanyManagerAppraisalMeetingStatusTableRowDto,
  filters: AppraisalMonitoringFiltersState,
): boolean {
  const employeeLevels = filters.selectedEmployeeLevels?.value
  if (
    employeeLevels != null &&
    employeeLevels.length > 0 &&
    !employeeLevels.includes(row.employee.currentLevel as (typeof employeeLevels)[number])
  ) {
    return false
  }
  if (
    !!filters.selectedBranchOffice &&
    (filters.selectedBranchOffice.id ? row.office?.id !== filters.selectedBranchOffice.id : !!row.office)
  ) {
    return false
  }
  if (!!filters.selectedBusinessUnit && row.businessUnit.id !== filters.selectedBusinessUnit.businessUnit.id) {
    return false
  }
  if (!!filters.selectedBuManager && !row.buManagers.some((manager) => manager.id === filters.selectedBuManager!.id)) {
    return false
  }

  return true
}

export function computeFilteredAppraisalMonitoringRows(
  allRows: CompanyManagerAppraisalMeetingStatusTableRowDto[],
  filters: AppraisalMonitoringFiltersState,
): CompanyManagerAppraisalMeetingStatusTableRowDto[] {
  return allRows.filter((row) => matchesAppraisalMonitoringFiltersArea(row, filters))
}

export type CompanyManagerAppraisalMeetingStatusCounters = {
  status: CompanyManagerAppraisalMeetingStatus
  count: number
}

export function computeAppraisalMonitoringCountersFromRows(
  rows: CompanyManagerAppraisalMeetingStatusTableRowDto[],
): CompanyManagerAppraisalMeetingStatusCounters[] {
  const counts = new Map<CompanyManagerAppraisalMeetingStatus, number>()
  for (const status of Object.values(CompanyManagerAppraisalMeetingStatus)) {
    counts.set(status, 0)
  }
  for (const row of rows) {
    counts.set(row.status, (counts.get(row.status) ?? 0) + 1)
  }
  return Object.values(CompanyManagerAppraisalMeetingStatus).map((status) => ({
    status,
    count: counts.get(status) ?? 0,
  }))
}
